import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';

interface SidebarContextType {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  isMobileView: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // Default to open on desktop
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024; // Tailwind's lg breakpoint
      setIsMobileView(mobile);
      if (mobile) {
        setIsSidebarOpen(false); // Force closed (minimized) on mobile
      } else {
        setIsSidebarOpen(true); // Default to open on desktop
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    // On mobile, toggling opens/closes it (typically as an overlay)
    // On desktop, it switches between full and icon-rail
    setIsSidebarOpen(prev => !prev);
  };

  return (
    <SidebarContext.Provider value={{
      isSidebarOpen,
      toggleSidebar,
      isMobileView
    }}>
      {children}
    </SidebarContext.Provider>
  );
};

export function useSidebar(): SidebarContextType {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
} 